// MACHETA ÎNTREGII CLĂDIRI, pentru instalații: toate nivelurile deodată,
// translucide — plăcile și pereții doar cât să dea reperul —, iar peste ele
// stratul instalațiilor (`strat3d`, fără fereastră de nivel). Ca macheta
// structurii (`structure/fem/Scene3D`): clădirea întreagă se citește dintr-o
// privire, coloanele de la subsol la acoperiș, colectorul sub ele.
//
// Geometria stă în cadrul documentului (+y sud); cine o arată o pune în
// `lume` (`plan/oglinda3d`), care face întoarcerea. Nimic de aici nu se alege
// cu clicul: clădirea e fundalul, instalația e subiectul.

import * as THREE from "three";
import type { CanonicalDoc, StoreyPlan } from "../viewer/canonical";
import { scena3D } from "../viewer/plan/arh3d";

export interface Cladire3D {
  grup: THREE.Group;
  /** Cutia clădirii, în cadrul documentului: de ea se încadrează camera. */
  cutie: THREE.Box3;
  elibereaza(): void;
}

/** Cât de străvezii sunt pereții și plăcile: destul ca o țeavă din perete sau
 *  de sub placă să se vadă prin ele de oriunde, destul ca etajele să se numere. */
const OPACITATE_PERETE = 0.12;
const OPACITATE_PLACA = 0.2;

export function construiesteCladire(plansee: readonly StoreyPlan[], doc: CanonicalDoc): Cladire3D {
  const grup = new THREE.Group();
  grup.name = "cladire";
  const deArs: { dispose(): void }[] = [];
  const matPerete = new THREE.MeshStandardMaterial({ color: 0xd9d4c9, transparent: true, opacity: OPACITATE_PERETE,
                                                     depthWrite: false, roughness: 0.95 });
  const matPlaca = new THREE.MeshStandardMaterial({ color: 0xcfc9bc, transparent: true, opacity: OPACITATE_PLACA,
                                                    depthWrite: false, roughness: 0.95, side: THREE.DoubleSide });
  const matContur = new THREE.LineBasicMaterial({ color: 0x8a847a, transparent: true, opacity: 0.7 });
  const cub = new THREE.BoxGeometry(1, 1, 1);
  deArs.push(matPerete, matPlaca, matContur, cub);

  const scena = scena3D(plansee, doc, [0, 0]);
  const m4 = new THREE.Matrix4(), q = new THREE.Quaternion(), ax = new THREE.Vector3(0, 0, 1);
  scena.corpuri.forEach((corp, i) => {
    const plan = plansee[i];
    const g = new THREE.Group();
    g.name = plan?.id ?? corp.cod;
    g.position.z = plan?.cota ?? 0;
    // placa: fața ei translucidă și conturul, ca etajele să se numere
    for (const inel of corp.placa) {
      if (inel.length < 3) continue;
      const geo = new THREE.ShapeGeometry(new THREE.Shape(inel.map(([x, y]) => new THREE.Vector2(x, y))));
      const contur = new THREE.BufferGeometry().setFromPoints(inel.map(([x, y]) => new THREE.Vector3(x, y, 0.01)));
      deArs.push(geo, contur);
      g.add(new THREE.Mesh(geo, matPlaca), new THREE.LineLoop(contur, matContur));
    }
    // pereții: cutii pe axa lor, cu grosimea și înălțimea lor (ca în `Randare3D`)
    if (corp.pereti.length) {
      const zid = new THREE.InstancedMesh(cub, matPerete, corp.pereti.length);
      corp.pereti.forEach((w, k) => {
        const dx = w.b[0] - w.a[0], dy = w.b[1] - w.a[1];
        const L = Math.hypot(dx, dy);
        if (!(L > 0)) { zid.setMatrixAt(k, m4.makeScale(0, 0, 0)); return; }
        q.setFromAxisAngle(ax, Math.atan2(dy, dx));
        zid.setMatrixAt(k, m4.compose(new THREE.Vector3(w.a[0] + dx / 2, w.a[1] + dy / 2, w.h / 2), q,
                                      new THREE.Vector3(L, w.t, w.h)));
      });
      zid.instanceMatrix.needsUpdate = true;
      zid.computeBoundingSphere();
      g.add(zid);
    }
    grup.add(g);
  });
  const cutie = new THREE.Box3().setFromObject(grup);
  return {
    grup,
    cutie,
    elibereaza: () => {
      grup.removeFromParent();
      deArs.forEach((x) => x.dispose());
    },
  };
}
